import type { Product } from '@/pages/cardapio/types';

interface ProductCardProps {
  product: Product;
  quantity: number;
  isSelected: boolean;
  onSelect: () => void;
  onQuantity: (qty: number) => void;
  onAddToCart: () => void;
}

const ProductCard = ({
  product,
  quantity,
  isSelected,
  onSelect,
  onQuantity,
  onAddToCart,
}: ProductCardProps) => {
  return (
    <article
      className="group flex flex-col overflow-hidden rounded-2xl border border-background-200/70 bg-background-100 transition-colors duration-500 hover:border-secondary-500/40"
      data-product-shop
    >
      {/* Image */}
      <div className="relative aspect-[4/3] overflow-hidden bg-background-200">
        <img
          src={product.image}
          alt={product.name}
          title={`${product.name} — Sanduíche Natural Naturalle`}
          className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-background-100/80 via-transparent to-transparent" />

        {/* Tags */}
        <div className="absolute left-3 top-3 flex flex-wrap gap-1.5">
          {product.tags.map((tag) => (
            <span
              key={tag}
              className="rounded-full border border-secondary-500/25 bg-background-50/60 px-2.5 py-1 text-[11px] font-medium text-foreground-900 backdrop-blur-sm"
            >
              {tag}
            </span>
          ))}
        </div>

        {/* Weight */}
        <div className="absolute bottom-3 right-3">
          <span className="rounded-full border border-secondary-500/25 bg-background-50/60 px-3 py-1 text-[11px] font-semibold text-foreground-900 backdrop-blur-sm">
            {product.weight}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col p-5">
        <span className="mb-2 font-label text-[11px] font-medium uppercase tracking-wider text-secondary-500">
          {product.category}
        </span>
        <h3 className="mb-2 font-heading text-base font-semibold text-foreground-900 transition-colors group-hover:text-secondary-400">
          {product.name}
        </h3>
        <p className="mb-5 flex-1 text-sm leading-relaxed text-foreground-500">
          {product.description}
        </p>

        {/* Quantity selector */}
        <div className="mb-4 flex items-center justify-between border-t border-background-200/70 pt-4">
          <span className="text-xs font-medium text-foreground-500">Quantidade</span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => onQuantity(quantity - 1)}
              aria-label="Diminuir quantidade"
              className="flex h-7 w-7 items-center justify-center rounded-full border border-background-300/70 text-foreground-400 transition-colors hover:border-secondary-500/50 hover:text-secondary-400 cursor-pointer"
            >
              <i className="ri-subtract-line text-xs" />
            </button>
            <span className="w-8 text-center font-label text-sm font-semibold text-foreground-900">
              {quantity}
            </span>
            <button
              onClick={() => onQuantity(quantity + 1)}
              aria-label="Aumentar quantidade"
              className="flex h-7 w-7 items-center justify-center rounded-full border border-background-300/70 text-foreground-400 transition-colors hover:border-secondary-500/50 hover:text-secondary-400 cursor-pointer"
            >
              <i className="ri-add-line text-xs" />
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={onSelect}
            className="flex-1 whitespace-nowrap rounded-full border border-background-300/70 px-4 py-2.5 font-label text-sm font-semibold text-foreground-400 transition-colors hover:border-secondary-500/50 hover:text-secondary-400 cursor-pointer"
          >
            {isSelected ? 'Fechar Detalhes' : 'Ver Detalhes'}
          </button>
          <button
            onClick={onAddToCart}
            className="flex items-center gap-1.5 whitespace-nowrap rounded-full bg-accent-500 px-4 py-2.5 font-label text-sm font-semibold text-white transition-colors hover:bg-accent-400 cursor-pointer"
          >
            <i className="ri-add-line" />
            Carrinho
          </button>
        </div>
      </div>
    </article>
  );
};

export default ProductCard;